// @ts-nocheck
import {
    chain,
    RequestDataFromApi as BaseRequestDataFromApi,
    SortCollections as BaseSortCollections,
    ExecuteOnSuccessCallback as BaseExecuteOnSuccessCallback,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ExtractHandlerParameter, ExtractHandlerProps } from "@store/Framework/Types/Common";
import { addToEndOfChain, HasOnError, replaceInChain } from "@toolkit/Modules/client-framework/HandlerCreator";

type HandlerChainType = typeof BaseRequestDataFromApi;

export interface LoadProductParameter extends ExtractHandlerParameter<HandlerChainType>, HasOnError {
    handleNotFoundError?: boolean;
}

export interface LoadProductProps extends ExtractHandlerProps<HandlerChainType> {
    hasError?: boolean;
}

replaceInChain<LoadProductParameter, LoadProductProps>(chain, BaseRequestDataFromApi, async props => {
    // The original handler chain and API do not trap errors, to prevent breaking changes we only trap errors if asked to.
    if (props.parameter.onError || props.parameter.handleNotFoundError) {
        try {
            await BaseRequestDataFromApi(props);
        } catch (error) {
            // A 404 status indicates the provided product ID does not exist. We want to mark the data as loaded in this case to prevent potential API calls with the same ID.
            if (
                props.parameter.handleNotFoundError &&
                error &&
                typeof error === "object" &&
                "status" in error &&
                error.status === 404
            ) {
                props.hasError = true;

                const notFoundModel: Partial<ProductModel> = {
                    id: props.parameter.id,
                    canonicalUrl: props.parameter.id,
                    isNotFound: true,
                };

                props.apiResult = notFoundModel as ProductModel;

                return;
            }

            // For any other error, call the onError callback and re-throw the error to be handled by the global error handler.
            props.parameter.onError?.();
            throw error;
        }

        return;
    }

    await BaseRequestDataFromApi(props);
});

replaceInChain<LoadProductParameter, LoadProductProps>(chain, BaseSortCollections, async props => {
    if (props.hasError) {
        // Prevent the sort handler from running if there was an error loading the product, as the product data will not be valid.
        return;
    }

    await BaseSortCollections(props);
});

replaceInChain<LoadProductParameter, LoadProductProps>(chain, BaseExecuteOnSuccessCallback, props => {
    if (props.hasError) {
        // Prevent the onSuccess callback from running if there was an error loading the product.
        return;
    }

    BaseExecuteOnSuccessCallback(props);
});

addToEndOfChain<LoadProductParameter, LoadProductProps>(chain, props => {
    if (props.hasError) {
        props.parameter.onError?.();
    }
});
